import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEmployeeVacations,
  getCompanyVacations,
  getVacationsCalendar,
  updateVacationStatusApi,
  updateVacationRequestApi,
  createVacationRequestApi,
  updateMyVacationRequestApi,
  cancelMyVacationRequestApi,
  checkAvailabilityApi,
  getOverlappingTasksApi,
  notifyReporterApi,
} from "../api/vacationApi";

export const useGetEmployeeVacations = (employeeId, month, year, options = {}) => {
  return useQuery({
    queryKey: ["employeeVacations", employeeId, month, year, !!options.includePending],
    queryFn: () => getEmployeeVacations(employeeId, month, year, options),
    enabled: !!employeeId,
  });
};

export const useGetCompanyVacations = (month, year, enabled = true) => {
  return useQuery({
    queryKey: ["companyVacations", month, year],
    queryFn: () => getCompanyVacations(month, year),
    enabled,
    staleTime: 15 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

export const useGetVacationsCalendar = (month, year, enabled = true) => {
  return useQuery({
    queryKey: ["vacationsCalendar", month, year],
    queryFn: () => getVacationsCalendar(month, year),
    enabled: enabled && !!month && !!year,
    staleTime: 15 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

export const useUpdateVacationStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateVacationStatusApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vacations"] });
      queryClient.invalidateQueries({ queryKey: ["myVacations"] });
      queryClient.invalidateQueries({ queryKey: ["vacationsCalendar"] });
      queryClient.invalidateQueries({ queryKey: ["companyVacations"] });
      queryClient.invalidateQueries({ queryKey: ["employeeVacations"] });
    },
  });
};

export const useUpdateVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateVacationRequestApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vacations"] });
      queryClient.invalidateQueries({ queryKey: ["myVacations"] });
      queryClient.invalidateQueries({ queryKey: ["vacationsCalendar"] });
      queryClient.invalidateQueries({ queryKey: ["companyVacations"] });
      queryClient.invalidateQueries({ queryKey: ["employeeVacations"] });
    },
  });
};

const refreshVacationQueries = (queryClient) => {
  ["vacations", "myVacations", "vacationsCalendar", "companyVacations", "employeeVacations"].forEach(
    (key) => queryClient.invalidateQueries({ queryKey: [key] })
  );
};

export const useUpdateMyVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMyVacationRequestApi,
    onSuccess: () => refreshVacationQueries(queryClient),
  });
};

export const useCancelMyVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelMyVacationRequestApi,
    onSuccess: () => refreshVacationQueries(queryClient),
  });
};

export const useCreateVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createVacationRequestApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vacations"] });
      queryClient.invalidateQueries({ queryKey: ["myVacations"] });
      queryClient.invalidateQueries({ queryKey: ["vacationsCalendar"] });
      queryClient.invalidateQueries({ queryKey: ["companyVacations"] });
      queryClient.invalidateQueries({ queryKey: ["employeeVacations"] });
    },
  });
};

export const useCheckAvailability = () => {
  return useMutation({
    mutationFn: ({ checks, excludeTaskId }) =>
      checkAvailabilityApi(checks, excludeTaskId),
  });
};

export const useGetOverlappingTasks = (vacationId) => {
  return useQuery({
    queryKey: ["overlappingTasks", vacationId],
    queryFn: () => getOverlappingTasksApi(vacationId),
    enabled: !!vacationId,
  });
};

export const useNotifyReporter = () => {
  return useMutation({
    mutationFn: ({ vacationId, taskId }) => notifyReporterApi({ vacationId, taskId }),
  });
};
